//@flow
import React from 'react';
import {
  TableContainer,
  makeStyles,
  Table,
  TableHead,
  TableCell,
  Typography,
  TableBody,
  TableRow,
  Hidden,
  MenuItem,
} from '@material-ui/core';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {Link} from 'react-router-dom/cjs/react-router-dom.min';
import {colors} from '../../../../assets/styles/colors';
import {useCrmSegments} from '../../utils';
import {getStringDate} from '../../../../utils/date';
import InlineLoader from '../../../../elements/LoadingSpinner/InlineLoader';
import CustomChip from '../../../../elements/CustomChip';
import {Pagination} from '@material-ui/lab';
import CustomSelect from '../../../../elements/CustomSelect/CustomSelect';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    padding: '1rem',
  },
  tableHeader: {
    background: colors.grey300,
  },
  tableHeaderText: {
    fontWeight: 600,
    fontSize: '.9rem',
  },

  tableRow: {
    '&:nth-of-type(even)': {
      background: '#F8F9FB',
    },
  },

  tableContainer: {
    marginBottom: '1rem',
    position: 'relative',
    scrollBehavior: 'smooth',
    background: 'white',
    borderRadius: '.75rem',
    boxShadow:
      '0px 0.5px 2px 0px rgba(96, 97, 112, 0.16), 0px 0px 1px 0px rgba(40, 41, 61, 0.08);',
    minHeight: '50vh',

    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
    },

    '&::-webkit-scrollbar-track': {
      background: colors.grey200,
      borderRadius: '5px',
    },

    '&::-webkit-scrollbar-thumb': {
      background: colors.grey400,
      borderRadius: '5px',
    },
  },
  emptyTableContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    padding: '3rem 1rem',
    width: '100%',
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
    background: 'rgba(255, 255, 255, .8)',
  },
  paginationArea: {
    display: 'flex',
    padding: '1rem',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
}));

export default function CrmSegmentTable() {
  const classes = useStyles();

  const {
    loading,
    segments: crmSegments,
    pagination,
    setPage,
    setSize,
  } = useCrmSegments();

  return (
    <div className={classes.root}>
      <TableContainer className={classes.tableContainer}>
        {loading && (
          <div className={classes.loader}>
            <InlineLoader loading size={32} />
          </div>
        )}
        <Table>
          <TableHead className={classes.tableHeader}>
            <TableCell>
              <Typography className={classes.tableHeaderText}>
                Nombre
              </Typography>
            </TableCell>
            <TableCell>
              <Typography className={classes.tableHeaderText}>Fecha</Typography>
            </TableCell>
            <TableCell>
              <Typography className={classes.tableHeaderText}>Tipo</Typography>
            </TableCell>
            <TableCell>
              <Typography className={classes.tableHeaderText}>
                Acciones
              </Typography>
            </TableCell>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {crmSegments.map(segment => (
              <TableRow key={segment.id} className={classes.tableRow}>
                <TableCell>{segment.name}</TableCell>
                <TableCell>{getStringDate(segment.createdAt)}</TableCell>
                <TableCell>
                  <CustomChip
                    size="small"
                    color={
                      segment.segmentType === 'MANUAL' ? 'primary' : 'secondary'
                    }
                    label={
                      segment.segmentType === 'MANUAL' ? 'Manual' : 'Dinámico'
                    }
                  />
                </TableCell>

                <TableCell>
                  <CustomButton
                    variant="outlined"
                    component={Link}
                    to={
                      segment.segmentType === 'MANUAL'
                        ? `/segments/manual/${segment.id}`
                        : `/segments/dynamic/${segment.id}`
                    }
                  >
                    Editar
                  </CustomButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className={classes.paginationArea}>
          <Pagination
            count={
              pagination &&
              (pagination.size >= pagination.total
                ? 1
                : Math.ceil(pagination.total / pagination.size))
            }
            className={classes.pagination}
            color="primary"
            page={pagination.page + 1}
            onChange={(e, page) => {
              setPage(page - 1);
            }}
          />
          <Hidden smUp>
            <CustomSelect
              native
              pagination
              disableUnderline
              className={classes.sizeSelect}
              value={pagination.size}
              onChange={e => setSize(e.target.value)}
            >
              <option value={8}>8 por página</option>
              <option value={16}>16 por página</option>
              <option value={24}>24 por página</option>
              <option value={32}>32 por página</option>
            </CustomSelect>
          </Hidden>
          <Hidden xsDown>
            <CustomSelect
              pagination
              disableUnderline
              className={classes.sizeSelect}
              value={pagination.size}
              onChange={e => setSize(e.target.value)}
            >
              <MenuItem value={8}>8 por página</MenuItem>
              <MenuItem value={16}>16 por página</MenuItem>
              <MenuItem value={24}>24 por página</MenuItem>
              <MenuItem value={32}>32 por página</MenuItem>
            </CustomSelect>
          </Hidden>
        </div>
        {crmSegments.length === 0 && (
          <aside className={classes.emptyTableContainer}>
            <Typography variant="body1">No hay segmentos</Typography>

            <CustomButton
              variant="outlined"
              component={Link}
              to="/segments/manual/new"
            >
              Crear segmento Manual
            </CustomButton>
            <CustomButton
              variant="outlined"
              component={Link}
              to="/segments/dynamic/new"
            >
              Crear segmento Dinámico
            </CustomButton>
          </aside>
        )}
      </TableContainer>
    </div>
  );
}
